import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { timeout } from 'ember-concurrency'
import { on } from '@ember/modifier'
import { t } from 'ember-intl'
import { optional } from 'teacher-ui-client/helpers/optional'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import {
  FormWrapperSubmitButton,
  type ExternalSubmitState,
} from 'teacher-ui-client/components/form-wrapper/submit-button'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import type Owner from '@ember/owner'

interface Args {
  schoolClass: SchoolClass
  onSubmit: (schoolClass: SchoolClass) => unknown
  onClose?: () => unknown
}

export class LeaveClassConfirmationForm extends Component<Args> {
  @tracked submitState: ExternalSubmitState = 'default'

  @tracked schoolClassName = ''

  @tracked isLastTeacher = false

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    // Set in the constructor so that it persists while the form is open, even if the teacher has been removed
    this.schoolClassName = this.args.schoolClass.name
    this.isLastTeacher = this.args.schoolClass.teachers.length === 1
  }

  @action
  async onSubmit(event: Event) {
    event.preventDefault()

    this.submitState = 'load'

    try {
      await this.args.onSubmit(this.args.schoolClass)

      await timeout(1000)
      await this.args.onClose?.()

      this.submitState = 'done'
    } catch {
      this.submitState = 'error'
    }
  }

  <template>
    <form {{on "submit" this.onSubmit}}>
      <div class="p-4">
        {{#if this.isLastTeacher}}
          <p class="mb-3">{{t
              "manage.manageClasses.classes.leaveClass.confirmationLastTeacher"
              className=this.schoolClassName
              htmlSafe=true
            }}</p>
        {{/if}}

        <p class="mb-3">{{t
            "manage.manageClasses.classes.leaveClass.confirmation"
            className=this.schoolClassName
            htmlSafe=true
          }}</p>
      </div>
      <div class="border-dusty-black-50 border-t p-2 text-right">
        <UiButton data-test-cancel type="reset" {{on "click" (optional @onClose)}}>
          {{t "manage.manageClasses.classes.leaveClass.cancel"}}
        </UiButton>
        <FormWrapperSubmitButton
          data-test-submit
          @defaultText={{t "manage.manageClasses.classes.leaveClass.ok"}}
          @state={{this.submitState}}
          @appearance={{ButtonAppearance.regular}}
        />
      </div>
    </form>
  </template>
}

export default LeaveClassConfirmationForm
